/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Road Closure family (Ice Storm Risk →
 *  "Road Closures"; operator-cop-doctrine §3A.7 context-locking / §3B.8 the
 *  AMBIENT register / §4.5 ice-storm "downed trees/branches off roads").
 *
 *  This module is the SINGLE source of the road-closure palette. It is consumed
 *  ONLY by the road-closures family:
 *    - RoadClosuresLayer map markers (the ambient register-coloured dot) + the
 *      ground-clamped affected-segment polyline
 *    - the RoadClosureBody side-panel register badge + legend
 *
 *  Per §3B.8: SHAPE encodes "a road closure" (a round dot anchoring an affected
 *  road SEGMENT line — the line-along-the-road is the family's visual signature,
 *  distinct from the outage family's area polygon); COLOR encodes the REGISTER
 *  (full closure vs partial restriction) within the family; MOTION is NOT in this
 *  family's budget (closures are ambient, never pulse). COLOR is never load-bearing
 *  alone — every render pairs the hue with the register label (panel badge +
 *  legend), exactly like the outage / community-status pattern.
 *
 *  Register hues (deliberately the road-closure family's OWN bronze/road tokens,
 *  the real-world roadwork register):
 *    closure       barricade bronze  #d2691e   (full road closure)
 *    restriction   muted gold        #c9b458   (lane / partial restriction)
 *    unknown       neutral grey      #9ca3af   (unmapped / status unknown)
 *
 *  Overlap debt this lock acknowledges (§3A.7): bronze is an orange cousin of the
 *  fire-stage / OC-Monitored oranges, and the muted gold a cousin of the outage
 *  amber, that mean other things in OTHER families. Those families own their own
 *  hexes; this module owns its OWN bronze/gold and must never import a sibling
 *  palette. SHAPE (a dot anchoring a road-segment LINE, not a flame / outage-area
 *  polygon / pulsing anomaly dot) keeps the families distinguishable in monochrome.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots / ./lifecycleStates / ./sectorStyles /
 *     ./outageStyles / ./aircraftRoles / ./floodGauges
 *     (enforced by the no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** The register key — derived from the `is_closure` boolean. */
export type ClosureRegister = 'closure' | 'restriction';

export function closureRegister(isClosure: boolean): ClosureRegister {
  return isClosure ? 'closure' : 'restriction';
}

/** The per-register bronze/gold hexes — the single source of truth. */
export const ROAD_CLOSURE_COLORS: Record<ClosureRegister, string> = {
  closure: '#d2691e', // barricade bronze — full road closure
  restriction: '#c9b458', // muted gold — lane / partial restriction
};

/** Neutral grey when the register can't be resolved (never dropped). */
export const ROAD_CLOSURE_DEFAULT_COLOR = '#9ca3af';

/** Short human label per register, paired with the colour so hue is never alone. */
export const ROAD_CLOSURE_REGISTER_LABEL: Record<ClosureRegister, string> = {
  closure: 'Full closure',
  restriction: 'Restriction',
};

/** The map CALLOUT-BOX headline per register (#127 Pinecrest mockup). Short, ALL
 *  CAPS — the box reads at a glance the way roadside signage does. Colour is still
 *  paired with text here (the box hue + the word), so hue is never load-bearing. */
export const ROAD_CLOSURE_CALLOUT_LABEL: Record<ClosureRegister, string> = {
  closure: 'ROAD CLOSED',
  restriction: 'LANE RESTRICTED',
};

/** Text colour that reads on the register-coloured callout box (bronze → white,
 *  light gold → night ink). Keeps the box legible without importing a sibling palette. */
export const ROAD_CLOSURE_CALLOUT_TEXT: Record<ClosureRegister, string> = {
  closure: '#ffffff',
  restriction: '#0b1020',
};

export interface RoadClosureStyle {
  /** CSS hex for the segment line + callout box + badge. */
  color: string;
  /** Register label — colour is never load-bearing alone. */
  label: string;
  /** ALL-CAPS callout-box headline for the map ("ROAD CLOSED" / "LANE RESTRICTED"). */
  callout: string;
  /** Text colour that reads on the register-coloured callout box. */
  textColor: string;
  register: ClosureRegister;
}

/** Resolve the render style for a closure's register (from `is_closure`). */
export function roadClosureStyle(isClosure: boolean | null | undefined): RoadClosureStyle {
  const register = closureRegister(isClosure === true);
  return {
    color: ROAD_CLOSURE_COLORS[register] ?? ROAD_CLOSURE_DEFAULT_COLOR,
    label: ROAD_CLOSURE_REGISTER_LABEL[register],
    callout: ROAD_CLOSURE_CALLOUT_LABEL[register],
    textColor: ROAD_CLOSURE_CALLOUT_TEXT[register],
    register,
  };
}

/** Affected-segment polyline width (px) + opacity — the SOLID LINE draping the
 *  closed road is this family's primary signature (#127). A full closure reads
 *  heavier than a partial restriction. */
export function roadClosureLineWidth(isClosure: boolean | null | undefined): number {
  return isClosure ? 6 : 4;
}
export const ROAD_CLOSURE_LINE_ALPHA = 0.95;

/** Leader-line geometry for the callout box (#127): a thin world-space stalk rising
 *  from the road anchor up to the floating box. Height in metres — kept modest so the
 *  box hugs the road at regional zoom and lifts off as the operator descends. */
export const ROAD_CLOSURE_CALLOUT_HEIGHT_M = 2400;
export const ROAD_CLOSURE_LEADER_WIDTH = 2.5;

/** Normalized severity → short label, for the detail panel (paired, not a hue). */
export const ROAD_CLOSURE_SEVERITY_LABEL: Record<string, string> = {
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  unknown: 'Unknown',
};
